// The setup board: what this workspace has done, what it should do next, and one
// button that does it.
//
// It is rendered by AppShell rather than by the pages it names, which is what
// keeps Connections, Compose and Media unaware that this module exists.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { COMPANY } from "../lib/company";
import { useWorkspace } from "../lib/WorkspaceContext";
import { coachLine, MILESTONE_COUNT } from "./milestones";
import { MILESTONE_ICON, SparkleMark, Tick } from "./icons";
import { useEngagement } from "./EngagementContext";

export function ProgressStrip() {
  const { ready, progress, refresh, openTour, stripCollapsed, stripExpanded, collapseStrip } = useEngagement();
  const { active } = useWorkspace();
  const [retrying, setRetrying] = useState(false);
  const navigate = useNavigate();
  const workspaceId = active?.workspace_id;

  // Completion and unavailable counts must not remove the way back to coaching.
  if (!workspaceId) return null;
  const unavailable = !ready || !progress;

  const connect = progress?.steps[0] ?? null;
  const linked = Boolean(connect?.done);
  const keepMinimized = linked && !stripExpanded;

  if (stripCollapsed || keepMinimized || ((unavailable || progress?.complete) && !stripExpanded)) {
    return (
      <div className="eng-mini-wrap">
        <button
          type="button"
          className="eng-mini"
          onClick={() => {
            collapseStrip(false);
            requestAnimationFrame(() => {
              const board = document.getElementById("simino-coach-board");
              board?.focus({ preventScroll: true });
              board?.scrollIntoView({ block: "start", behavior: "instant" });
            });
          }}
          aria-expanded={false}
          aria-label="Open Simino Social setup card"
          title={progress && ready
            ? `${progress.doneCount} of ${MILESTONE_COUNT} setup steps complete — open setup card`
            : "Open Simino Social setup card"}
        >
          <span className="eng-mini-badge" aria-hidden="true">
            <SparkleMark />
            {progress && <span>{progress.doneCount}/{MILESTONE_COUNT}</span>}
          </span>
          <span>Setup</span>
        </button>
      </div>
    );
  }

  const minimize = (
    <button
      type="button"
      className="eng-minimize sm"
      onClick={() => {
        collapseStrip(true);
      }}
      aria-expanded={true}
      aria-label="Minimize Simino Social setup card"
    >
      <span aria-hidden="true">^</span>
    </button>
  );

  if (unavailable || !progress) {
    return (
      <section id="simino-coach-board" tabIndex={-1} className="eng-board blocked" aria-label="Simino Coach">
        <div className="eng-board-copy" role="status">
          <h2>Simino Coach</h2>
          <p className="eng-board-sub">
            {!ready || retrying ? "Loading your setup progress…" : "Your setup progress could not be loaded. Please try again."}
          </p>
        </div>
        <button type="button" className="sm" disabled={!ready || retrying} onClick={async () => {
          setRetrying(true);
          try { await refresh(); } finally { setRetrying(false); }
        }}>Try again</button>
        {minimize}
      </section>
    );
  }

  // The one case a checklist cannot help with: nothing is connected and this
  // person is not allowed to connect anything. Every step below it is blocked by
  // a decision that is not theirs to make, so say that instead of listing tasks.
  if (connect && !connect.done && !connect.allowed) {
    return (
      <section id="simino-coach-board" tabIndex={-1} className="eng-board blocked" role="status">
        <div className="eng-board-copy">
          <h2>Nothing is connected yet</h2>
          <p className="eng-board-sub">{connect.milestone.delegated}</p>
        </div>
        <button type="button" className="sm" onClick={() => openTour(0)}>
          What happens when they do
        </button>
        {minimize}
      </section>
    );
  }

  const percent = Math.round((progress.doneCount / MILESTONE_COUNT) * 100);
  const next = progress.next;
  const productName = COMPANY.product.replace(/\s+/g, "");

  return (
    <section id="simino-coach-board" tabIndex={-1} className="eng-board eng-setup-card" aria-label="Setup progress">
      <div className="eng-board-head">
        <div className="eng-board-copy">
          <h2>
            {progress.complete
              ? "Your setup is complete"
              : <>Get <span>{productName}</span><br />working for you</>}
          </h2>
          <p className="eng-board-sub">
            Finish setup to start publishing, scheduling, and tracking your content across connected channels.
          </p>
        </div>

        <div className="eng-setup-orb" aria-hidden="true">
          <SparkleMark />
        </div>
        {minimize}
      </div>

      <div className="eng-progress-line">
        <strong>{progress.doneCount} of {MILESTONE_COUNT} setup steps complete</strong>
        <span>{percent}%</span>
      </div>
      <div
        className="eng-meter"
        role="progressbar"
        aria-valuenow={progress.doneCount}
        aria-valuemin={0}
        aria-valuemax={MILESTONE_COUNT}
        aria-label="Setup steps complete"
      >
        <span style={{ width: `${percent}%` }} />
      </div>

      <ol className="eng-step-list">
        {progress.steps.map((s, i) => {
          const Icon = MILESTONE_ICON[s.milestone.id];
          const isNext = s === next;
          return (
            <li key={s.milestone.id}>
              <button
                type="button"
                className={`eng-step-row${s.done ? " done" : ""}${isNext ? " next" : ""}${!s.allowed ? " delegated" : ""}`}
                onClick={() => openTour(i)}
              >
                <span className="eng-step-icon" aria-hidden="true"><Icon /></span>
                <span className="eng-step-title">{s.done ? s.milestone.doneTitle : s.milestone.title}</span>
                <span className="eng-step-state" aria-hidden="true">
                  {s.done ? <Tick /> : ""}
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      <button
        type="button"
        className="primary eng-continue"
        onClick={() => next ? navigate(next.milestone.route) : collapseStrip(true)}
      >
        {next ? "Continue setup" : "Done for now"} <span aria-hidden="true">→</span>
      </button>
      <button type="button" className="link eng-dismiss" onClick={() => collapseStrip(true)}>
        Dismiss for now
      </button>
      <p className="eng-foot-note eng-setup-note">
        <SparkleMark /> {coachLine(progress.doneCount)}
      </p>
    </section>
  );
}
